/**
 * Turn mapped row fields into one routing string, then resolve API bucket + UI flow label.
 */
export function buildRoutePath(mapped) {
  if (mapped.flow_routing) {
    return String(mapped.flow_routing).trim();
  }
  const parts = [
    mapped.route_main,
    mapped.route_category,
    mapped.route_subcategory,
    mapped.description,
  ].filter((x) => x !== "" && x != null);
  return parts.map((x) => String(x).trim()).join(" > ");
}

function matchTransportOutgoing(pathLower) {
  return /transportation of goods to consumer|to consumer|outgoing.*transport/.test(
    pathLower
  );
}

function matchTransportIngoing(pathLower) {
  return (
    /transportation of goods to company|goods transport.*to company|to company/.test(
      pathLower
    ) ||
    (/transport/.test(pathLower) &&
      /purchased plastics|ingoing|supplier|transportation of goods to/.test(pathLower) &&
      !matchTransportOutgoing(pathLower))
  );
}

function matchEnergy(pathLower, mapped) {
  if (/process emissions/i.test(String(mapped.route_category || ""))) {
    return false;
  }
  return (
    /district heating|fjernvarme|electricity|heat and process energy|purchased electricity|natural gas/.test(
      pathLower
    ) ||
    (/energy and processes/.test(pathLower) &&
      /heat|district|electric|steam|cooling/.test(pathLower))
  );
}

function isWaterPurchaseRow(mapped, pathLower) {
  const unit = String(mapped.unit || "").toLowerCase().trim();
  const desc = String(mapped.description || "").toLowerCase();
  if (/vandforbrug|water consumption|tap water/i.test(pathLower)) return true;
  if (unit === "l" && /cleaning|vand|water/i.test(desc)) return true;
  if (
    unit === "l" &&
    /purchase/i.test(pathLower) &&
    /cleaning|water|vand/i.test(`${pathLower} ${desc}`)
  ) {
    return true;
  }
  return false;
}

/**
 * @returns {{ key: string, displayFlow: string, flowLabel: string }}
 */
export function resolveFlow(mapped) {
  const routePath = buildRoutePath(mapped);
  const pathLower = routePath.toLowerCase();
  const gas = String(mapped.gas_type || "");

  if (/hfc|pfc|nf3|sf6|greenhouse/i.test(gas)) {
    return {
      key: "industrial_gas",
      displayFlow: "5",
      flowLabel: "Industrial / process gas",
    };
  }

  if (
    /process emissions/i.test(String(mapped.route_category || "")) &&
    /hfc|greenhouse|gas/i.test(gas + pathLower)
  ) {
    return {
      key: "industrial_gas",
      displayFlow: "5",
      flowLabel: "Industrial / process gas",
    };
  }

  if (matchTransportOutgoing(pathLower)) {
    return {
      key: "transport_outgoing",
      displayFlow: "4.2",
      flowLabel: "Outgoing transport",
    };
  }

  if (matchTransportIngoing(pathLower)) {
    return {
      key: "transport_ingoing",
      displayFlow: "4.1",
      flowLabel: "Ingoing transport",
    };
  }

  if (matchEnergy(pathLower, mapped)) {
    return {
      key: "energy_main",
      displayFlow: "2.1",
      flowLabel: "Purchased energy",
    };
  }

  /* Order: EoL sold products before generic “waste” */
  if (
    /end-of-life treatment of sold|end-of-life.*sold products|sold products.*end-of-life/i.test(
      pathLower
    )
  ) {
    return {
      key: "final_treatment_unsold",
      displayFlow: "18.3",
      flowLabel: "Final treatment (sold products)",
    };
  }

  if (
    /waste and recycling/i.test(pathLower) ||
    (/^\s*waste\s*>/i.test(routePath) && /plast|plastic|waste type/i.test(pathLower))
  ) {
    return {
      key: "waste_surplus",
      displayFlow: "10",
      flowLabel: "Surplus materials / waste",
    };
  }

  if (isWaterPurchaseRow(mapped, pathLower)) {
    return {
      key: "water_locational",
      displayFlow: "2.4",
      flowLabel: "Water (location)",
    };
  }

  if (/purchase|products and services/i.test(pathLower)) {
    return {
      key: "purchased_supply",
      displayFlow: "8",
      flowLabel: "Purchased goods (supplies)",
    };
  }

  return {
    key: "unknown",
    displayFlow: "?",
    flowLabel: "Unmapped category",
  };
}

export function rowHasRequiredMapping(mapped, mapping) {
  const targets = Object.values(mapping);
  const hasQty = targets.includes("quantity");
  const hasUnit = targets.includes("unit");
  const hasRoute =
    targets.includes("flow_routing") || targets.includes("route_main");
  return hasQty && hasUnit && hasRoute;
}

/** Flow keys that the importer can POST to an API today */
export const AUTOMATED_FLOW_KEYS = new Set([
  "energy_main",
  "transport_ingoing",
  "transport_outgoing",
  "industrial_gas",
  "water_locational",
  "purchased_supply",
  "waste_surplus",
  "final_treatment_unsold",
]);
